import { Injectable, NgZone } from '@angular/core';
import { AkitaFirebaseStore } from './firebase.store';
import { applyTransaction, logAction } from '@datorama/akita';
import { UserModel } from '@app/shared/models/api/user.model';
import { VerifyUserModel } from '@app/shared/models/api/auth/verify-user.model';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { AuthAPIService } from '@app/akita/api/auth/services/auth.service';
import { configureScope } from '@sentry/browser';

@Injectable({ providedIn: 'root' })
export class AkitaFirebaseService {
  constructor(
    private readonly store: AkitaFirebaseStore,
    private readonly authAPIService: AuthAPIService,
    private readonly zone: NgZone
  ) {}

  public clearStore(): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction(`clearStore - Firebase Service`);

        // console.log('Clearing Firebase Store');
        this.store.reset();
      });
    });
  }

  public setCurrentUser(user: any): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction(`setCurrent User`);

        // console.log('🛠️ Firebase User:', user);
        this.store.setToken(user.accessToken);
        this.store.setFirebaseUser(user);
      });
    });
  }

  public setApiUser(user: UserModel): void {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction(`setApiUser - Done`);

        // console.log('👤 API User:', user);
        this.store.setApiUser(user);
      });
    });
  }

  public checkIfEmailIsValid(email: string): Observable<VerifyUserModel | null> {
    this.zone.run(() => {
      applyTransaction(() => {
        logAction('checkIfEmailIsValid()');
      });
    });

    return this.authAPIService.verifyUserDetails(email).pipe(
      tap(() => {
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('checkIfEmailIsValid() - done');
            // console.log('Email verification details:', details);
          });
        });
      }),
      catchError((error: unknown) => {
        const parsed = parseApiError(error);
        this.zone.run(() => {
          applyTransaction(() => {
            logAction('checkIfEmailIsValid() - error');
          });
        });
        return throwError(() => parsed);
      })
    );
  }

  public setSessionId(sessionId?: string | null): void {
    configureScope((scope) => {
      scope.setExtra('psid', `${sessionId || '??'}`);
    });

    this.zone.run(() => {
      applyTransaction(() => {
        logAction('setSessionId()');
        this.store.setSessionId(sessionId || '');
      });
    });
  }

  public updateOptIn(optIn: boolean | null): Observable<boolean> {
    const user = this.store.getValue().user;
    if (user) {
      user.opt_in = optIn;
      this.zone.run(() => {
        applyTransaction(() => {
          logAction('udateProfileInformation() - done');
          this.store.setApiUser(user);
        });
      });

      return of(this.store.getValue().user?.opt_in || false);
    }
    return of(false);
  }

  /*  @transaction()
  @action('requestPermissionStarted()')
  private requestPermissionStarted(id: string | null): void {
    this.store.update({
      error: null,
      requestingPermission: true,
      userId: id,
      lastUpdated: new Date(),
    });
  }
 */
  /*  @transaction()
  @action('requestPermissionFailed()')
  private requestPermissionFailed(error: unknown): void {
    this.store.update({
      error: error as any,
      requestingPermission: false,
      token: null,
      lastUpdated: new Date(),
    });
  } */
}
